// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import {
  CheckCircle,
  ArrowRight,
  Building2,
  Globe,
  User,
  Lock,
  Mail,
  Briefcase,
  Eye,
  EyeOff,
  CreditCard,
  ShieldCheck,
} from 'lucide-react'
import { GuardRokIcon } from '../components/GuardRokLogo'
import ChatWidget from '../components/ChatWidget'

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '')

const PLANS = [
  {
    id: 'solo_basic',
    label: 'Solo Basic',
    price: '$29',
    seats: 1,
    features: [
      '1 user · unlimited prompts',
      '14 AI tools',
      '7-day trial',
      'Sensitive-data policies',
    ],
  },
  {
    id: 'solo_pro',
    label: 'Solo Pro',
    price: '$49',
    seats: 1,
    features: [
      '1 user · unlimited prompts',
      '14 AI tools',
      '7-day trial',
      'SafeScrub™ Redact + Rewrite',
    ],
  },
  {
    id: 'starter',
    label: 'Starter',
    price: '$99',
    seats: 10,
    features: ['5–10 seats', 'All compliance packs', 'Custom policy builder', '14 AI tools'],
  },
  {
    id: 'professional',
    label: 'Professional',
    price: '$199',
    seats: 25,
    features: [
      '11–25 seats',
      'Compliance hub doc ingestion',
      'All compliance packs',
      '14 AI tools',
    ],
  },
  {
    id: 'business',
    label: 'Business',
    price: '$399',
    seats: 50,
    features: ['26–50 seats', 'SafeScrub™ Redact', 'Compliance hub doc ingestion', '14 AI tools'],
  },
  {
    id: 'enterprise',
    label: 'Enterprise',
    price: '$699',
    seats: 9999,
    features: ['51–100+ seats', 'SSO / SCIM', 'SafeScrub™ full suite', '7-year logs (SOX)'],
  },
]

const STEPS = ['Company', 'Admin Account', 'Plan', 'Payment', 'Done']

function CardStep({
  clientSecret,
  planLabel,
  planPrice,
  onSuccess,
  onBack,
}: {
  clientSecret: string
  planLabel: string
  planPrice: string
  onSuccess: () => void
  onBack: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [cardComplete, setCardComplete] = useState(false)
  const [cardName, setCardName] = useState('')

  async function handleSubmit() {
    if (!stripe || !elements) return
    setError('')
    if (!cardName.trim()) {
      setError('Please enter the name on your card.')
      return
    }
    setLoading(true)

    const card = elements.getElement(CardElement)
    if (!card) {
      setLoading(false)
      return
    }

    const { error: stripeErr } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: { card, billing_details: { name: cardName.trim() } },
    })

    if (stripeErr) {
      setError(stripeErr.message ?? 'Card error. Please try again.')
      setLoading(false)
      return
    }

    // Webhook (setup_intent.succeeded) will save pm + start trial on the org
    onSuccess()
    setLoading(false)
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-white mb-1">Add payment method</h2>
        <p className="text-gray-500 text-sm">
          Your card won't be charged until after your 7-day trial or 500 prompts — whichever comes
          first.
        </p>
      </div>

      {/* Trial summary */}
      <div
        style={{
          background: '#0f172a',
          border: '1px solid #1e3a5f',
          borderRadius: 12,
          padding: '16px 20px',
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <span style={{ color: '#94a3b8', fontSize: 13 }}>Plan</span>
          <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
            {planLabel} · {planPrice}/mo
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <span style={{ color: '#94a3b8', fontSize: 13 }}>Trial period</span>
          <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 13 }}>7 days free</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#94a3b8', fontSize: 13 }}>Due today</span>
          <span style={{ color: '#fff', fontWeight: 800, fontSize: 15 }}>$0.00</span>
        </div>
      </div>

      {/* Name on card */}
      <div style={{ marginBottom: 16 }}>
        <label
          style={{
            display: 'block',
            color: '#9ca3af',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 8,
          }}
        >
          Name on card
        </label>
        <input
          value={cardName}
          onChange={e => setCardName(e.target.value)}
          placeholder="Jane Smith"
          style={{
            width: '100%',
            background: '#111827',
            border: '1px solid #374151',
            borderRadius: 12,
            padding: '14px 16px',
            color: '#fff',
            fontSize: 14,
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Stripe card element */}
      <div style={{ marginBottom: 20 }}>
        <label
          style={{
            display: 'block',
            color: '#9ca3af',
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            marginBottom: 8,
          }}
        >
          <CreditCard className="w-3 h-3" style={{ display: 'inline', marginRight: 4 }} />
          Card details
        </label>
        <div
          style={{
            background: '#111827',
            border: '1px solid #374151',
            borderRadius: 12,
            padding: '14px 16px',
          }}
        >
          <CardElement
            options={{
              style: {
                base: {
                  color: '#fff',
                  fontSize: '14px',
                  fontFamily: 'system-ui, sans-serif',
                  '::placeholder': { color: '#4b5563' },
                },
                invalid: { color: '#ef4444' },
              },
            }}
            onChange={e => {
              setCardComplete(e.complete)
              if (e.error) setError(e.error.message ?? '')
              else setError('')
            }}
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-950 border border-red-800 text-red-400 text-sm rounded-xl px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={loading || !cardComplete || !stripe}
        className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2"
      >
        {loading ? 'Saving card...' : 'Start free trial →'}
      </button>

      <button
        onClick={onBack}
        className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 mt-2 transition-colors"
      >
        ← Back
      </button>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          marginTop: 16,
        }}
      >
        <ShieldCheck className="w-3.5 h-3.5 text-gray-600" />
        <span style={{ color: '#4b5563', fontSize: 11 }}>
          256-bit SSL · Secured by Stripe · Cancel anytime
        </span>
      </div>
    </div>
  )
}

export default function Signup() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Form data
  const [company, setCompany] = useState({ name: '', domain: '', industry: '' })
  const [admin, setAdmin] = useState({ fullName: '', email: '', password: '', confirmPassword: '' })
  const [plan, setPlan] = useState('solo_basic')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [orgId, setOrgId] = useState('')
  const [clientSecret, setClientSecret] = useState('')

  // If user is already logged in and visits /signup cold (step 0), redirect to dashboard.
  // But if they're mid-signup (step > 0), let them stay — they need to complete the card step.
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && step === 0) navigate('/dashboard', { replace: true })
    })
  }, [])

  // Pre-select plan from ?plan= query param (coming from Pricing page)
  useEffect(() => {
    const p = searchParams.get('plan')
    if (p && PLANS.find(x => x.id === p)) setPlan(p)
  }, [])

  const INDUSTRIES = [
    'Healthcare',
    'Banking & Finance',
    'Education',
    'Legal',
    'Government',
    'Real Estate',
    'Retail & E-Commerce',
    'Technology',
    'Manufacturing',
    'Insurance',
    'HR & People Ops',
    'Energy & Utilities',
    'Pharmaceutical & Biotech',
    'Telecommunications',
    'Cryptocurrency & Fintech',
    'Accounting & Audit',
    'APAC / International',
    'Media & Entertainment',
    'Consulting',
    'Other',
  ]

  async function handleCompanyNext() {
    setError('')
    if (!company.name.trim()) return setError('Company name is required')
    if (!company.domain.trim()) return setError('Company domain is required')
    if (!company.domain.includes('.')) return setError('Enter a valid domain (e.g. company.com)')
    if (!company.industry) return setError('Please select your industry')

    // Check if domain already registered → send them to sign in instead
    const { data } = await supabase
      .from('organizations')
      .select('id')
      .eq('domain', company.domain.toLowerCase())
      .single()
    if (data) {
      navigate('/login?hint=' + encodeURIComponent(company.domain))
      return
    }

    setStep(1)
  }

  async function handleAdminNext() {
    setError('')
    if (!admin.fullName.trim()) return setError('Full name is required')
    if (!admin.email.trim()) return setError('Email is required')
    if (!admin.email.includes('@')) return setError('Enter a valid email address')
    if (!admin.email.toLowerCase().endsWith('@' + company.domain.toLowerCase())) {
      return setError(`Email must match your company domain (@${company.domain})`)
    }
    if (admin.password.length < 8) return setError('Password must be at least 8 characters')
    if (admin.password !== admin.confirmPassword) return setError('Passwords do not match')
    setStep(2)
  }

  // Step 2 → 3: create org + auth user, then move to card collection
  async function handleSignup() {
    setError('')
    setLoading(true)

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: admin.email,
        password: admin.password,
        options: {
          data: {
            full_name: admin.fullName,
            company_name: company.name.trim(),
            company_domain: company.domain.toLowerCase().trim(),
            industry: company.industry,
            plan, // trigger uses this to assign user vs admin role
          },
        },
      })
      if (authError) throw authError
      if (!authData.user) throw new Error('Failed to create account')

      const userId = authData.user.id
      const selectedPlan = PLANS.find(p => p.id === plan)

      const { data: rpcResult } = await supabase.rpc('create_org_and_user', {
        p_org_name: company.name.trim(),
        p_org_domain: company.domain.toLowerCase().trim(),
        p_org_plan: 'free',
        p_org_seats: selectedPlan?.seats ?? 5,
        p_org_settings: {
          retention_days:
            plan === 'enterprise'
              ? 2555
              : plan === 'professional'
                ? 365
                : plan === 'starter'
                  ? 90
                  : 30,
          allowed_tools: ['chatgpt', 'claude', 'copilot', 'gemini'],
          notification_emails: [admin.email],
          risk_score_alert_threshold: 75,
          require_justification_on_warn: true,
          gdpr_region: 'US',
          industry: company.industry,
        },
        p_user_id: userId,
        p_user_full_name: admin.fullName.trim(),
        p_user_email: admin.email,
      })

      // org_id from RPC — may be empty if the auth trigger beat the RPC to it.
      // Fall back to querying the user's org directly.
      let createdOrgId = (rpcResult?.org_id as string) || ''
      if (!createdOrgId) {
        const { data: userRow } = await supabase
          .from('users')
          .select('org_id')
          .eq('id', userId)
          .single()
        createdOrgId = userRow?.org_id || ''
      }
      if (!createdOrgId) throw new Error('Failed to set up your workspace. Please try again.')

      if (createdOrgId) {
        try {
          await installDefaultPolicies(createdOrgId, userId)
        } catch {
          /* trigger handled it */
        }
      }

      // Fire MAYA welcome email — non-blocking
      supabase.functions
        .invoke('maya', {
          body: {
            org_id: createdOrgId,
            step: 1,
            org_email: admin.email,
            org_name: company.name.trim(),
            industry: company.industry,
            send_to: admin.email,
          },
        })
        .catch(e => console.warn('[maya step-1]:', e))

      // Create SetupIntent so the next step can collect the card
      const { data: siData, error: siErr } = await supabase.functions.invoke(
        'stripe-setup-intent',
        {
          body: { org_id: createdOrgId, email: admin.email },
        }
      )
      if (siErr || !siData?.client_secret)
        throw new Error('Failed to initialise payment. Please try again.')

      setOrgId(createdOrgId)
      setClientSecret(siData.client_secret)
      setStep(3) // → card step
    } catch (err: any) {
      setError(err.message || 'Failed to create account. Please try again.')
    }
    setLoading(false)
  }

  // Step 3 → 4: called by CardStep after Stripe confirms the card
  function handleCardSaved() {
    setStep(4)
  }

  async function installDefaultPolicies(orgId: string, userId: string) {
    // Always install SSN and Credit Card policies for every new org
    const defaultPolicies = [
      {
        name: 'Block SSN / Tax ID',
        description:
          'Prevents employees from sharing Social Security Numbers or Tax IDs with AI tools.',
        action: 'block',
        priority: 100,
        is_active: true,
        conditions: { match: 'any', rules: [{ detection_type: 'ssn' }] },
        applies_to_roles: ['admin', 'manager', 'user'],
        applies_to_tools: ['chatgpt', 'claude', 'copilot', 'gemini'],
        rules: [
          {
            name: 'SSN Pattern',
            type: 'ssn',
            pattern: '\\d{3}-\\d{2}-\\d{4}',
            severity: 'critical',
          },
          {
            name: 'SSN Keywords',
            type: 'custom_keyword',
            pattern: '\\b(social security number|ssn|tax id|taxpayer id|itin)\\b',
            severity: 'medium',
          },
        ],
      },
      {
        name: 'Redact Credit Card Numbers',
        description:
          'Automatically redacts credit card PANs from prompts before they reach AI tools.',
        action: 'redact',
        priority: 85,
        is_active: true,
        conditions: { match: 'any', rules: [{ detection_type: 'credit_card' }] },
        applies_to_roles: ['admin', 'manager', 'user'],
        applies_to_tools: ['chatgpt', 'claude', 'copilot', 'gemini'],
        rules: [
          {
            name: 'Visa Card',
            type: 'credit_card',
            pattern: '(?<!\\d)4[0-9]{3}[\\s\\-]?[0-9]{4}[\\s\\-]?[0-9]{4}[\\s\\-]?[0-9]{4}(?!\\d)',
            severity: 'critical',
          },
          {
            name: 'Mastercard',
            type: 'credit_card',
            pattern:
              '(?<!\\d)(5[1-5][0-9]{2}|2[2-7][0-9]{2})[\\s\\-]?[0-9]{4}[\\s\\-]?[0-9]{4}[\\s\\-]?[0-9]{4}(?!\\d)',
            severity: 'critical',
          },
        ],
      },
      {
        name: 'Block API Key Leaking',
        description: 'Detects and blocks prompts containing API keys or secrets.',
        action: 'block',
        priority: 95,
        is_active: true,
        conditions: { match: 'any', rules: [{ detection_type: 'api_key' }] },
        applies_to_roles: ['admin', 'manager', 'user'],
        applies_to_tools: ['chatgpt', 'claude', 'copilot', 'gemini'],
        rules: [
          {
            name: 'OpenAI / Anthropic Key',
            type: 'api_key',
            pattern: 'sk-[a-zA-Z0-9\\-_]{20,}',
            severity: 'critical',
          },
          {
            name: 'AWS Access Key',
            type: 'api_key',
            pattern: '(AKIA|AGPA|AIPA|ANPA|ANVA|ASIA)[A-Z0-9]{16}',
            severity: 'critical',
          },
        ],
      },
    ]

    for (const policy of defaultPolicies) {
      const { rules, ...policyData } = policy
      const { data: p, error: e } = await supabase
        .from('policies')
        .insert({
          ...policyData,
          org_id: orgId,
          created_by: userId,
          applies_to_departments: null,
        })
        .select()
        .single()

      if (e || !p) {
        console.error('Policy install failed:', policy.name, e)
        continue
      }
      const { error: ruleErr } = await supabase
        .from('detection_rules')
        .insert(rules.map(r => ({ ...r, org_id: orgId, policy_id: p.id, is_active: true })))
      if (ruleErr) console.error('Rule install failed for policy', policy.name, ruleErr)
    }
  }

  function passwordStrength(pw: string) {
    let score = 0
    if (pw.length >= 8) score++
    if (pw.length >= 12) score++
    if (/[A-Z]/.test(pw)) score++
    if (/[0-9]/.test(pw)) score++
    if (/[^A-Za-z0-9]/.test(pw)) score++
    if (score <= 1) return { score, label: 'Weak', color: '#ef4444' }
    if (score <= 3) return { score, label: 'Medium', color: '#f59e0b' }
    return { score, label: 'Strong', color: '#22c55e' }
  }

  function generatePassword() {
    const up = 'ABCDEFGHJKMNPQRSTUVWXYZ',
      lo = 'abcdefghjkmnpqrstuvwxyz'
    const di = '23456789',
      sp = '!@#$%^&*'
    const all = up + lo + di + sp
    const pw = [up, lo, di, sp].map(s => s[Math.floor(Math.random() * s.length)])
    for (let i = 4; i < 14; i++) pw.push(all[Math.floor(Math.random() * all.length)])
    return pw.sort(() => Math.random() - 0.5).join('')
  }

  const inputClass =
    'w-full bg-gray-900 border border-gray-700 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/20 transition-all placeholder-gray-600'
  const labelClass = 'block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-2'

  return (
    <div style={{ minHeight: '100vh', background: '#030712' }} className="flex">
      {/* Left Panel — Branding */}
      <div
        className="hidden lg:flex flex-col justify-between w-[420px] flex-shrink-0 p-12"
        style={{ background: '#0a0f1e', borderRight: '1px solid #1f2937' }}
      >
        <div>
          <div className="flex items-center gap-3 mb-16">
            <GuardRokIcon size={40} />
            <div>
              <div className="text-white font-bold text-lg leading-none">GuardRok™</div>
              <div className="text-gray-500 text-xs uppercase tracking-widest mt-0.5">
                AI Firewall
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h1 className="text-3xl font-bold text-white leading-tight mb-4">
              Protect your team from AI data leaks
            </h1>
            <p className="text-gray-400 text-sm leading-relaxed">
              GuardRok intercepts sensitive data before it reaches ChatGPT, Claude, Copilot, and
              Gemini. Setup takes 5 minutes.
            </p>
          </div>

          <div className="space-y-4">
            {[
              'Works on ChatGPT, Claude, Copilot & Gemini',
              'HIPAA, SOX, GDPR, PCI-DSS compliance packs',
              'Real-time dashboard and violation alerts',
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="w-3 h-3 text-red-400" />
                </div>
                <span className="text-gray-300 text-sm">{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-gray-800 rounded-xl p-4">
          <div className="text-gray-500 text-xs mb-2">Trusted for compliance under</div>
          <div className="flex flex-wrap gap-2">
            {['HIPAA', 'SOX', 'GDPR', 'PCI-DSS', 'FERPA', 'FINRA'].map(l => (
              <span
                key={l}
                className="text-xs bg-gray-800 text-gray-400 px-2 py-1 rounded-md font-mono"
              >
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel — Form */}
      <div className="flex-1 flex flex-col items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <GuardRokIcon size={30} />
            <span className="text-white font-bold">GuardRok™</span>
          </div>

          {/* Progress steps */}
          {step < 4 && (
            <div className="flex items-center gap-2 mb-8">
              {STEPS.slice(0, 4).map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                      i < step
                        ? 'bg-red-500 text-white'
                        : i === step
                          ? 'bg-red-500 text-white ring-4 ring-red-500/20'
                          : 'bg-gray-800 text-gray-500'
                    }`}
                  >
                    {i < step ? <CheckCircle className="w-4 h-4" /> : i + 1}
                  </div>
                  <span
                    className={`text-xs font-medium ${i === step ? 'text-white' : 'text-gray-600'}`}
                  >
                    {s}
                  </span>
                  {i < 3 && (
                    <div className={`w-6 h-px ${i < step ? 'bg-red-500' : 'bg-gray-800'}`} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── STEP 0: Company ── */}
          {step === 0 && (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-1">Set up your company</h2>
                <p className="text-gray-500 text-sm">
                  We'll create a GuardRok workspace for your organization.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    <Building2 className="w-3 h-3 inline mr-1" />
                    Company Name
                  </label>
                  <input
                    value={company.name}
                    onChange={e => setCompany(p => ({ ...p, name: e.target.value }))}
                    placeholder="Acme Corporation"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <Globe className="w-3 h-3 inline mr-1" />
                    Company Domain
                  </label>
                  <input
                    value={company.domain}
                    onChange={e =>
                      setCompany(p => ({ ...p, domain: e.target.value.toLowerCase() }))
                    }
                    placeholder="acme.com"
                    className={inputClass}
                  />
                  <p className="text-gray-600 text-xs mt-1.5">
                    All employees with @{company.domain || 'yourcompany.com'} email will auto-join
                  </p>
                </div>
                <div>
                  <label className={labelClass}>
                    <Briefcase className="w-3 h-3 inline mr-1" />
                    Industry
                  </label>
                  <select
                    value={company.industry}
                    onChange={e => setCompany(p => ({ ...p, industry: e.target.value }))}
                    className={inputClass + ' appearance-none'}
                    style={{ background: '#111827' }}
                  >
                    <option value="">Select your industry</option>
                    {INDUSTRIES.map(i => (
                      <option key={i} value={i}>
                        {i}
                      </option>
                    ))}
                  </select>
                </div>
                {error && (
                  <div className="bg-red-950 border border-red-800 text-red-400 text-sm rounded-xl px-4 py-3">
                    {error}
                  </div>
                )}
                <button
                  onClick={handleCompanyNext}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 1: Admin Account ── */}
          {step === 1 && (
            <div>
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-white mb-1">Create your admin account</h2>
                <p className="text-gray-500 text-sm">
                  You'll be the super admin for{' '}
                  <span className="text-white font-medium">{company.name}</span>.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    <User className="w-3 h-3 inline mr-1" />
                    Full Name
                  </label>
                  <input
                    value={admin.fullName}
                    onChange={e => setAdmin(p => ({ ...p, fullName: e.target.value }))}
                    placeholder="Jane Smith"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label className={labelClass}>
                    <Mail className="w-3 h-3 inline mr-1" />
                    Work Email
                  </label>
                  <input
                    type="email"
                    value={admin.email}
                    onChange={e => setAdmin(p => ({ ...p, email: e.target.value }))}
                    placeholder={`you@${company.domain}`}
                    className={inputClass}
                  />
                  <p className="text-gray-600 text-xs mt-1.5">
                    Must be a @{company.domain} email address
                  </p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass} style={{ marginBottom: 0 }}>
                      <Lock className="w-3 h-3 inline mr-1" />
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        const pw = generatePassword()
                        setAdmin(p => ({ ...p, password: pw, confirmPassword: pw }))
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: 11,
                        color: '#e63946',
                        padding: 0,
                        fontWeight: 600,
                      }}
                    >
                      ✦ Suggest password
                    </button>
                  </div>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={admin.password}
                      onChange={e => setAdmin(p => ({ ...p, password: e.target.value }))}
                      placeholder="Min 8 characters"
                      className={inputClass + ' pr-10'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {admin.password &&
                    (() => {
                      const s = passwordStrength(admin.password)
                      return (
                        <div style={{ marginTop: 8 }}>
                          <div
                            style={{
                              height: 4,
                              background: '#1f2937',
                              borderRadius: 4,
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                height: '100%',
                                width: `${(s.score / 5) * 100}%`,
                                background: s.color,
                                borderRadius: 4,
                                transition: 'all 0.3s',
                              }}
                            />
                          </div>
                          <div
                            style={{ fontSize: 11, color: s.color, marginTop: 4, fontWeight: 600 }}
                          >
                            {s.label} password
                          </div>
                        </div>
                      )
                    })()}
                </div>
                <div>
                  <label className={labelClass}>
                    <Lock className="w-3 h-3 inline mr-1" />
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={admin.confirmPassword}
                      onChange={e => setAdmin(p => ({ ...p, confirmPassword: e.target.value }))}
                      placeholder="Repeat password"
                      className={inputClass + ' pr-10'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors"
                    >
                      {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {error && (
                  <div className="bg-red-950 border border-red-800 text-red-400 text-sm rounded-xl px-4 py-3">
                    {error}
                  </div>
                )}
                <button
                  onClick={handleAdminNext}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setStep(0)
                    setError('')
                  }}
                  className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 transition-colors"
                >
                  ← Back
                </button>
              </div>
            </div>
          )}

          {/* ── STEP 2: Plan ── */}
          {step === 2 && !clientSecret && (
            <div>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-white mb-1">Choose your plan</h2>
                <p className="text-gray-500 text-sm">
                  7-day trial included. Team plans billed annually — you'll be redirected to
                  checkout after signup.
                </p>
              </div>
              <div className="space-y-3 mb-6">
                {PLANS.map(p => (
                  <div
                    key={p.id}
                    onClick={() => setPlan(p.id)}
                    className={`border rounded-xl p-4 cursor-pointer transition-all ${
                      plan === p.id
                        ? 'border-red-500 bg-red-500/10'
                        : 'border-gray-800 hover:border-gray-700 bg-gray-900/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            plan === p.id ? 'border-red-500' : 'border-gray-600'
                          }`}
                        >
                          {plan === p.id && <div className="w-2 h-2 rounded-full bg-red-500" />}
                        </div>
                        <span className="text-white font-semibold text-sm">{p.label}</span>
                        {p.id === 'starter' && (
                          <span className="text-xs bg-red-500/20 text-red-400 px-2 py-0.5 rounded-full font-medium">
                            Popular
                          </span>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="text-white font-bold">{p.price}</span>
                        <span className="text-gray-500 text-xs">/mo</span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 ml-7">
                      {p.features.map((f, i) => (
                        <span key={i} className="text-xs text-gray-400">
                          {i > 0 ? '·' : ''} {f}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {error && (
                <div className="bg-red-950 border border-red-800 text-red-400 text-sm rounded-xl px-4 py-3 mb-4">
                  {error}
                </div>
              )}
              <button
                onClick={handleSignup}
                disabled={loading}
                className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2"
              >
                {loading ? 'Creating your workspace...' : 'Create GuardRok Workspace'}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
              <button
                onClick={() => {
                  setStep(1)
                  setError('')
                }}
                className="w-full text-gray-500 hover:text-gray-300 text-sm py-2 mt-2 transition-colors"
              >
                ← Back
              </button>
              <p className="text-center text-gray-600 text-xs mt-4">
                By signing up you agree to our{' '}
                <a href="/terms" className="text-gray-400 underline hover:text-gray-300">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="text-gray-400 underline hover:text-gray-300">
                  Privacy Policy
                </a>
              </p>
            </div>
          )}

          {/* ── STEP 3: Card ── */}
          {step === 3 && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <CardStep
                clientSecret={clientSecret}
                planLabel={PLANS.find(p => p.id === plan)?.label ?? plan}
                planPrice={PLANS.find(p => p.id === plan)?.price ?? ''}
                onSuccess={handleCardSaved}
                onBack={() => {
                  setStep(2)
                  setError('')
                }}
              />
            </Elements>
          )}

          {/* ── STEP 4: Success ── */}
          {step === 4 && (
            <div className="text-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">You're all set! 🎉</h2>
              <p className="text-gray-400 text-sm mb-8 leading-relaxed">
                Your GuardRok workspace for{' '}
                <span className="text-white font-medium">{company.name}</span> is ready. 3 default
                policies have been installed automatically.
              </p>

              <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-6 text-left space-y-3">
                <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">
                  Next Steps
                </div>
                {[
                  {
                    n: '1',
                    t: 'Sign into your dashboard',
                    d: 'View real-time prompt monitoring',
                    href: null,
                  },
                  {
                    n: '2',
                    t: 'Install Chrome extension',
                    d: 'Add GuardRok to your browser',
                    href: 'https://chromewebstore.google.com/detail/guardrok-ai-firewall/dldblbcpofdflbbhpahbokamikpakhdh',
                  },
                  {
                    n: '3',
                    t: 'Invite your team',
                    d: 'They auto-join when signing up with @' + company.domain,
                    href: null,
                  },
                  {
                    n: '4',
                    t: 'Install compliance packs',
                    d: 'HIPAA, SOX, GDPR and more in Compliance Hub',
                    href: null,
                  },
                ].map(({ n, t, d, href }) => (
                  <div key={n} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-red-400 text-xs font-bold">{n}</span>
                    </div>
                    <div>
                      {href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-white text-sm font-medium underline decoration-red-500/50 hover:decoration-red-400"
                        >
                          {t} →
                        </a>
                      ) : (
                        <div className="text-white text-sm font-medium">{t}</div>
                      )}
                      <div className="text-gray-500 text-xs">{d}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate('/')}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl py-3 text-sm transition-colors flex items-center justify-center gap-2"
              >
                Go to Dashboard <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
      <ChatWidget />
    </div>
  )
}
